import * as React from "react";

import { Dialog, Button, Spinner, Alert } from "@/components/ui";
import { PromptPreviewObject, PostRequest } from "@/interfaces";
import { PostServices } from "@/services";
import { MentionsStep, PlayersStep, DeliveryOptionsStep, ReviewStep, SuccessStep } from "../PostFlowComonents";
import "./styles.scss";
import { SharedPostFlowDefaults } from "@/components/PostDialogs/SharedPostFlowDefaults";
import { usePostFlowContext, PostFlowProvider } from "../SharedPostFlowContext";
import { useSocialCoachSession } from "@/utils";
import { useQueryClient } from "@tanstack/react-query";

enum Step {
  DELIVERY = 1,
  PLAYERS = 2,
  MENTIONS = 3,
  REVIEW = 4,
  SUCCESS = 5,
}

interface Props {
  prompt: PromptPreviewObject;
  open: boolean;
  handleClose: () => void;
}

export function CoachPostDialog(props: Props) {
  const flowDefaults: SharedPostFlowDefaults = {
    promptId: props.prompt?.promptId,
    message: props.prompt?.message || "",
    mediaGroup: props.prompt?.post,
    addShortLink: props.prompt?.allowShortLink,
    addDisclaimer: props.prompt?.allowComplianceDisclaimer,
    hashtags: props.prompt?.hashtags,
  };
  return (
    <PostFlowProvider defaults={flowDefaults} key={props.prompt?.promptId || "new"}>
      <CoachPostDialogContent {...props} />
    </PostFlowProvider>
  );
}

function CoachPostDialogContent({ open, handleClose }: Props) {
  const queryClient = useQueryClient();
  const { coachId } = useSocialCoachSession();
  const [currentStep, setCurrentStep] = React.useState<Step>(Step.DELIVERY);
  const {
    promptId,
    deliveryOptions,
    selectedPlayerIds,
    addDefaultHashtags,
    addDisclaimer,
    addShortLink,
    buildPostRequest,
    isPosting,
    setIsPosting,
    setPostError,
    isDeliveryStepIncomplete,
    blockedWords,
    postError,
    postButtonLabel,
  } = usePostFlowContext();

  const handleConfirmPost = async (): Promise<any> => {
    try {
      setIsPosting(true);
      setPostError(undefined);
      const basePostRequest = buildPostRequest();
      const postRequest: PostRequest = {
        ...basePostRequest,
        coachId: coachId!,
        promptId: promptId!,
        playerIds: selectedPlayerIds,
        allowHashtags: addDefaultHashtags,
        allowShortLink: addShortLink,
        allowComplianceDisclaimer: addDisclaimer,
      };
      await PostServices.create(postRequest);
      queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
      setCurrentStep(Step.SUCCESS);
    } catch (e: any) {
      setPostError(e?.message || "Unable to complete your post. Please try again later.");
    } finally {
      setIsPosting(false);
    }
  };

  const stepConfigs: Record<
    Step,
    {
      title: string;
      isValid?: boolean;
      onNext?: () => void;
      onBack?: () => void;
    }
  > = {
    [Step.DELIVERY]: {
      title: "Delivery Options",
      isValid: !isDeliveryStepIncomplete,
      onNext: () => setCurrentStep(Step.PLAYERS),
    },
    [Step.PLAYERS]: {
      title: "Select Players",
      isValid: selectedPlayerIds.length > 0,
      onNext: () => setCurrentStep(Step.REVIEW),
      onBack: () => setCurrentStep(Step.DELIVERY),
    },
    [Step.MENTIONS]: {
      title: "Custom Mentions (Beta)",
      isValid: true,
      onNext: () => setCurrentStep(Step.REVIEW),
      onBack: () => setCurrentStep(Step.REVIEW),
    },
    [Step.REVIEW]: {
      title: "Final Review",
      isValid: blockedWords.length === 0,
      onBack: () => setCurrentStep(Step.PLAYERS),
    },
    [Step.SUCCESS]: {
      title: "Success!",
    },
  };

  const currentConfig = stepConfigs[currentStep];

  const renderStepContent = () => {
    switch (currentStep) {
      case Step.DELIVERY:
        return <DeliveryOptionsStep />;
      case Step.PLAYERS:
        return <PlayersStep />;
      case Step.REVIEW:
        return <ReviewStep onMentionsClick={() => setCurrentStep(Step.MENTIONS)} />;
      case Step.MENTIONS:
        return <MentionsStep />;
      case Step.SUCCESS:
        return <SuccessStep />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={open => !open && handleClose()}
      size={currentStep == Step.PLAYERS ? "3xl" : "2xl"}
      showCloseButton={!isPosting}
    >
      <Dialog.Header>
        <Dialog.Title>
          <div className={"flex flex-row"}>
            <div className={"border-r border-slate-300 pr-4 mr-4"}>
              {deliveryOptions.isAutoPost ? "New Post" : "Send Prompt"}
            </div>
            <div className="font-medium">{currentConfig.title}</div>
          </div>
        </Dialog.Title>
      </Dialog.Header>
      <Dialog.Content className="pb-6 max-h-[70vh] overflow-y-auto flex flex-col gap-4">
        {postError && (
          <Alert variant="destructive">
            <Alert.Title>Error submitting post</Alert.Title>
            <Alert.Description>{postError}</Alert.Description>
          </Alert>
        )}
        {renderStepContent()}
      </Dialog.Content>
      <Dialog.Footer>
        {currentStep === Step.SUCCESS ? (
          <Button onClick={() => handleClose()}>Done</Button>
        ) : (
          <div className="flex flex-row justify-end gap-2">
            {currentConfig.onBack && (
              <Button variant="outline" onClick={currentConfig.onBack} disabled={isPosting}>
                Back
              </Button>
            )}

            {currentStep < Step.REVIEW ? (
              <Button onClick={currentConfig.onNext} disabled={currentConfig.isValid === false}>
                Next
              </Button>
            ) : (
              <Button onClick={handleConfirmPost} disabled={isPosting} className="min-w-[140px]">
                {isPosting ? (
                  <>
                    <Spinner className="mr-2 size-4" />
                    Submitting...
                  </>
                ) : (
                  postButtonLabel
                )}
              </Button>
            )}
          </div>
        )}
      </Dialog.Footer>
    </Dialog>
  );
}
